/**
 * DeviceProtectionCard — PROTECTED-DEVICES-MANAGE-1D.
 *
 * Card "Proteção" da página Detalhes: mostra o estado EFETIVO de proteção do
 * dispositivo (ENV OR DB) vindo de `center.protection` (com fallback ao endpoint
 * GET /protection) e expõe as ações de gestão da camada MANUAL:
 *
 *   - Ativar proteção   → POST /api/admin/devices/{id}/protection/enable   (PLATFORM-ONLY)
 *   - Desativar proteção→ POST /api/admin/devices/{id}/protection/disable  (PLATFORM-ONLY)
 *
 * Regras (o backend é a fonte da verdade — aqui é só UX honesta):
 *   - source env/production/reference é IMUTÁVEL → NÃO há botão de desativar (o
 *     backend retorna 403). Só source='manual' (e não-imutável) é desativável.
 *   - enable/disable exigem confirmação forte (useCriticalConfirm) + motivo, e só
 *     aparecem para platform admin (isPlatformAdmin).
 *   - O ESTADO é SEMPRE visível, inclusive para quem não pode alterar.
 *
 * Frontend-only: usa endpoints já existentes (1C). NÃO cria comando/OTA; NÃO toca
 * HMAC/MAC/secret/device_id/firmware.
 */
import React, { useEffect, useState } from "react";

import {
  disableDeviceProtection,
  enableDeviceProtection,
  getDeviceProtection,
} from "../lib/api.js";
import { isPlatformAdmin } from "../lib/rbac.js";
import { useCriticalConfirm } from "./ConfirmCriticalAction.jsx";

const SOURCE_LABEL = {
  production: "Produção",
  reference: "Referência",
  env: "Ambiente (.env)",
  manual: "Manual",
};

function fmtDate(s) {
  if (!s) return "—";
  const d = new Date(s);
  return isNaN(d.getTime()) ? String(s) : d.toLocaleString("pt-BR");
}

function friendlyErr(e) {
  const s = e?.status;
  const m = e?.message || "";
  // O request() central reescreve toda 403 sem `reason` para um genérico, mas
  // preserva o detail original do backend em e.body.detail — é lá que vem
  // "proteção imutável (...)" no disable de produção/referência/env.
  const detail = e?.body?.detail || "";
  if (s === 401) return "Sessão expirada — faça login novamente.";
  if (s === 403) {
    if (/imut/i.test(m) || /imut/i.test(detail))
      return "Proteção de sistema — não pode ser desativada pela UI. " + (detail || m);
    return "Sem permissão (somente plataforma) ou confirmação recusada.";
  }
  if (s === 404) return "Device não encontrado.";
  if (s === 409) return "Conflito de estado (409).";
  if (s === 422) return `Dado inválido: ${m || "informe um motivo."}`;
  return m || "Falha na ação de proteção.";
}

function Row({ k, v, mono }) {
  return (
    <div className="flex justify-between gap-3 py-0.5">
      <span className="text-[11px] uppercase tracking-wide text-noc-faint">{k}</span>
      <span className={"text-[13px] text-noc-text text-right " + (mono ? "font-mono" : "")}>{v}</span>
    </div>
  );
}

export default function DeviceProtectionCard({ data, notify, onChanged }) {
  const confirmCritical = useCriticalConfirm();
  const deviceId = data?.device?.device_id;
  const platformAdmin = isPlatformAdmin();

  // Estado: center.protection é o primário; getDeviceProtection é fallback/refresh.
  const [prot, setProt] = useState(data?.protection || null);
  const [action, setAction] = useState(null); // 'enable' | 'disable'
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  // Sincroniza com o center quando ele recarrega (load/polling/onChanged).
  useEffect(() => {
    if (data?.protection) setProt(data.protection);
  }, [data?.protection]);

  // Fallback: se o center não trouxe protection (ex.: edge de migration), busca direto.
  useEffect(() => {
    let alive = true;
    if (!data?.protection && deviceId) {
      getDeviceProtection(deviceId)
        .then((p) => alive && setProt(p))
        .catch(() => {});
    }
    return () => {
      alive = false;
    };
  }, [data?.protection, deviceId]);

  async function refresh() {
    if (!deviceId) return;
    try {
      setProt(await getDeviceProtection(deviceId));
    } catch {
      /* mantém estado atual em falha de refresh */
    }
  }

  const known = prot != null; // estado de proteção carregado? (null = edge/migration)
  const effective = !!prot?.effective_protected;
  const source = prot?.source || null;
  const immutable = !!prot?.immutable;
  const isManual = source === "manual" && !immutable; // desativável
  const sourceLabel = source ? SOURCE_LABEL[source] || source : "—";

  const ACTIONS = {
    enable: {
      key: "enable",
      title: "Ativar proteção",
      danger: false,
      impact:
        "Liga a proteção MANUAL: o dispositivo passa a recusar comandos/OTA, " +
        "alteração de geo e remoção de vínculo até ser desprotegido.",
      successMsg: "Proteção ativada.",
      run: (a) => enableDeviceProtection(deviceId, a),
    },
    disable: {
      key: "disable",
      title: "Desativar proteção",
      danger: true,
      impact:
        "Remove a proteção MANUAL: o dispositivo volta a aceitar comandos/OTA, " +
        "geo e remoção de vínculo. Só funciona para proteção manual (produção/" +
        "referência/ambiente são imutáveis e o backend recusa com 403).",
      successMsg: "Proteção removida.",
      run: (a) => disableDeviceProtection(deviceId, a),
    },
  };

  function open(a) {
    setAction(a);
    setReason("");
    setError(null);
  }
  function close() {
    if (!busy) {
      setAction(null);
      setError(null);
    }
  }

  async function doConfirm() {
    if (!action) return;
    if (!reason.trim()) {
      setError("Informe o motivo (obrigatório).");
      return;
    }
    setError(null);
    try {
      const secret = await confirmCritical({
        actionLabel: action.title,
        target: { kind: "single", deviceId },
        impact: action.impact,
      });
      if (secret == null) return; // cancelou no diálogo de confirmação
      setBusy(true);
      await action.run({ reason: reason.trim(), confirm: secret });
      if (notify) notify("ok", action.successMsg);
      setBusy(false);
      setAction(null);
      await refresh();
      onChanged && onChanged();
    } catch (e) {
      setError(friendlyErr(e));
      setBusy(false);
    }
  }

  const btn = (cls) =>
    "h-9 px-3 rounded-lg text-xs font-semibold border transition-all hover:-translate-y-0.5 " + cls;
  const safe = "border-noc-border text-noc-text hover:border-noc-accent/50";
  const danger = "border-noc-err/30 text-noc-err bg-noc-err/10";

  return (
    <div className="glass-card-pad space-y-3">
      <div className="text-xs font-semibold uppercase tracking-wider text-noc-muted">
        Proteção
      </div>

      {/* Badge de status efetivo — SEMPRE visível */}
      <div className="flex items-center gap-2">
        {!known ? (
          <span className="px-2.5 py-1 rounded-md border text-[12px] font-semibold
                           bg-noc-bg border-noc-border text-noc-muted">
            Proteção indisponível
          </span>
        ) : effective ? (
          <span className="px-2.5 py-1 rounded-md border text-[12px] font-semibold
                           bg-noc-warn/15 text-noc-warn border-noc-warn/40">
            🔒 Protegido
          </span>
        ) : (
          <span className="px-2.5 py-1 rounded-md border text-[12px] font-semibold
                           bg-noc-ok/15 text-noc-ok border-noc-ok/40">
            Não protegido
          </span>
        )}
        {known && effective && (
          <span className="px-2 py-0.5 rounded-md border text-[11px] font-semibold
                           bg-noc-bg border-noc-border text-noc-muted">
            {immutable ? "Imutável" : "Manual"}
          </span>
        )}
      </div>

      {/* Avisos por tipo de proteção */}
      {!known && (
        <div className="text-[12px] bg-noc-bg border border-noc-border text-noc-muted
                        rounded-md px-3 py-2">
          Estado de proteção indisponível no momento (não foi possível carregar). Os
          guards do backend permanecem ativos.
        </div>
      )}
      {known && effective && immutable && (
        <div className="text-[12px] bg-noc-warn/10 border border-noc-warn/30 text-noc-warn
                        rounded-md px-3 py-2">
          Proteção de sistema. Este dispositivo só pode ser desprotegido por alteração
          controlada de configuração/deploy.
        </div>
      )}
      {known && effective && isManual && (
        <div className="text-[12px] bg-noc-bg border border-noc-border text-noc-muted
                        rounded-md px-3 py-2">
          Proteção manual operacional. Pode ser removida somente por super admin com
          confirmação forte.
        </div>
      )}
      {known && !effective && (
        <div className="text-[12px] bg-noc-err/10 border border-noc-err/30 text-noc-err
                        rounded-md px-3 py-2">
          Este dispositivo não está protegido contra ações operacionais críticas.
        </div>
      )}

      {/* Estado atual */}
      <div className="space-y-0.5">
        <Row k="Estado" v={!known ? "Indisponível" : effective ? "Protegido" : "Não protegido"} />
        <Row k="Origem" v={sourceLabel} />
        <Row k="Motivo" v={prot?.reason || "—"} />
        {prot?.created_by && <Row k="Criado por" v={prot.created_by} />}
        {prot?.created_at && <Row k="Criado em" v={fmtDate(prot.created_at)} />}
        {prot?.updated_by && <Row k="Atualizado por" v={prot.updated_by} />}
        {prot?.updated_at && <Row k="Atualizado em" v={fmtDate(prot.updated_at)} />}
        {prot?.expires_at && <Row k="Expira em" v={fmtDate(prot.expires_at)} />}
      </div>

      {/* Ações (platform-only) */}
      <div className="flex flex-wrap gap-2 pt-1">
        {platformAdmin && known && !effective && (
          <button className={btn(safe)} onClick={() => open(ACTIONS.enable)}>
            Ativar proteção
          </button>
        )}
        {platformAdmin && effective && isManual && (
          <button className={btn(danger)} onClick={() => open(ACTIONS.disable)}>
            Desativar proteção
          </button>
        )}
      </div>
      {!platformAdmin && (
        <div className="text-[11px] text-noc-faint">
          Somente super admin / plataforma pode alterar a proteção.
        </div>
      )}

      {/* Modal de revisão + motivo (a confirmação forte de senha abre em seguida) */}
      {action && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
          onClick={close}>
          <div className="bg-noc-panel border border-noc-border rounded-xl w-full max-w-md p-5 space-y-4"
            onClick={(e) => e.stopPropagation()}>
            <h2 className="text-[15px] font-semibold text-noc-text">{action.title}</h2>
            <div className={"text-[12px] rounded-md px-3 py-2 border "
              + (action.danger ? "bg-noc-err/10 border-noc-err/30 text-noc-err"
                               : "bg-noc-warn/10 border-noc-warn/30 text-noc-warn")}>
              {action.impact}
            </div>
            <div className="space-y-0.5">
              <Row k="device_id" v={deviceId || "—"} mono />
              <Row k="Origem atual" v={sourceLabel} />
            </div>
            <div className="space-y-1">
              <label className="text-[11px] uppercase tracking-wide text-noc-faint">
                Motivo (obrigatório)
              </label>
              <input className="w-full bg-noc-bg border border-noc-border rounded-md px-2 py-1.5
                                text-[13px] text-noc-text focus:border-noc-accent outline-none"
                value={reason} onChange={(e) => setReason(e.target.value)}
                placeholder={action.key === "enable"
                  ? "ex.: dispositivo crítico em produção"
                  : "ex.: liberado para manutenção aprovada"} />
            </div>
            {error && (
              <div className="text-[12px] bg-noc-err/10 border border-noc-err/30 text-noc-err
                              rounded-md px-3 py-2">{error}</div>
            )}
            <div className="flex items-center justify-end gap-2">
              <button className="btn-ghost" onClick={close} disabled={busy}>Cancelar</button>
              <button className={action.danger ? "btn-danger" : "btn-primary"}
                onClick={doConfirm} disabled={busy || !reason.trim()}>
                {busy ? "Processando…" : action.title}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
